// Landing.tsx
import React, { useState, useEffect } from "react";
import TopBar from "../../components/TopBar/TopBar";
import {
  Spin,
  Alert,
  Typography,
  Modal,
  Drawer,
  Tabs,
  Button,
  Space,
} from "antd";
import { MenuOutlined } from "@ant-design/icons";
import "./Landing.scss";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { getPopularCars } from "../../utils/getPopularCars";
import { getMakes, getModels, getWebsites } from "../../utils/getFilters";
import { Car, FilterValues } from "../../types/home";
import { formatCarData } from "../../utils/formatCarData";
import { searchCars, getMixedClassifieds } from "../../utils/searchCars";
import SearchFiltersSection from "../../components/SearchFiltersSection/SearchFiltersSection";
import TopPicksSection from "../../components/Tabs/TopPicksSection";
import ClassifiedsSection from "../../components/Tabs/ClassifiedsSection";
import WholesaleSection from "../../components/Tabs/WholesaleSection";
import FordCreditSection from "../../components/Tabs/FordCreditSection";
import TradeInsSection from "../../components/Tabs/TradeInSection";

const { Title } = Typography;
const { TabPane } = Tabs;

type TabKey = "topPicks" | "classifieds" | "wholesale" | "fordCredit" | "tradeIns";

const Landing: React.FC = () => {
  // Update TabKey type to include "fordCredit"
  type TabKey = "topPicks" | "classifieds" | "wholesale" | "fordCredit" | "tradeIns";

  // Mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [activeTab, setActiveTab] = useState<TabKey>("topPicks");

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const navigate = useNavigate();
  const [page, setPage] = useState<number>(() => Number(1));
  const [sortField, setSortField] = useState<string>(() => "");
  const [sortOrder, setSortOrder] = useState<string>(() => "");
  const [includeBuyersFee, setIncludeBuyersFee] = useState<boolean>(
    () => false
  );
  const [isClassifieds, setIsClassifieds] = useState<boolean>(false);

  // State for filters
  const [searchTerm, setSearchTerm] = useState("");
  const [filterValues, setFilterValues] = useState({
    selectedMakes: [] as string[],
    selectedModels: [] as string[],
    websites: [] as string[],
    reserveStatus: [] as string[],
    sellerType: [] as string[],
    pastToggle: [] as string[],
  });

  // State for custom filters (Top Picks)
  const [customFilterValues, setCustomFilterValues] = useState({
    selectedMakes: ["Dodge", "Jeep", "Chevrolet", "Ram"] as string[],
    selectedModels: [] as string[],
    websites: [
      "PCARMARKET",
      "Cars and Bids",
      "Bring a Trailer",
      "eBay Motors",
    ] as string[],
    reserveStatus: [] as string[],
    sellerType: ["Private"] as string[],
    pastToggle: [] as string[],
    startYear: "2014",
  });

  const [fordCustomFilterValues, setFordCustomFilterValues] = useState({
    selectedMakes: ["Ford"] as string[],
    selectedModels: [] as string[],
    websites: [
      "PCARMARKET",
      "Hagerty Marketplace",
      "Cars and Bids",
      "Bring a Trailer",
    ] as string[],
    reserveStatus: [] as string[],
    sellerType: [] as string[],
    pastToggle: [] as string[],
    startYear: "2016",
  });

  // Save table state to local storage
  const saveTableState = () => {
    const currentState = {
      page,
      itemsPerPage: 10,
      pastToggle: "current",
      searchTerm,
      sortField,
      sortOrder,
      includeBuyersFee,
      starToggle,
      filterValues: isClassifieds
        ? { ...filterValues, reserveStatus: [], sellerType: [] }
        : filterValues,
      isClassifieds,
    };
    localStorage.setItem("tableState", JSON.stringify(currentState));
  };

  // State for car data
  // Top Picks data
  const [topPicksCars, setTopPicksCars] = useState<Array<Car>>([]);
  const [topPicksLength, setTopPicksLength] = useState<number>(0);
  const [loadingTopPicks, setLoadingTopPicks] = useState<boolean>(false);

  // Classifieds data
  const [classifiedsCars, setClassifiedsCars] = useState<Array<Car>>([]);
  const [classifiedsLength, setClassifiedsLength] = useState<number>(0);
  const [loadingClassifieds, setLoadingClassifieds] = useState<boolean>(false);

  // Wholesale data
  const [wholesaleCars, setWholesaleCars] = useState<Array<Car>>([]);
  const [wholesaleLength, setWholesaleLength] = useState<number>(0);
  const [loadingWholesale, setLoadingWholesale] = useState<boolean>(false);

  // Ford Credit data
  const [fordCreditCars, setFordCreditCars] = useState<Array<Car>>([]);
  const [fordCreditLength, setFordCreditLength] = useState<number>(0);
  const [loadingFordCredit, setLoadingFordCredit] = useState<boolean>(false);

  // Trade Ins data
  const [tradeInsCars, setTradeInsCars] = useState<Array<Car>>([]);
  const [tradeInsLength, setTradeInsLength] = useState<number>(0);
  const [loadingTradeIns, setLoadingTradeIns] = useState<boolean>(false);

  // Common states
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;
  const [starToggle, setStarToggle] = useState<string>("all");
  const [starChanged, setStarChanged] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Dynamic options
  const [makes, setMakes] = useState<string[]>([]);
  const [models, setModels] = useState<string[]>([]);
  const [websites, setWebsites] = useState<string[]>([]);

  // Static classified sites that the backend /get_websites endpoint doesn't return
  const CLASSIFIED_SITES = ["Facebook", "Craigslist", "Autotrader"];

  // When in classifieds mode, merge static classified sites with any backend-returned sites
  const displayedWebsites = isClassifieds
    ? Array.from(new Set([...CLASSIFIED_SITES, ...websites]))
    : websites;

  // Modal state
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);

  // Drawer state (for mobile filters)
  const [drawerVisible, setDrawerVisible] = useState<boolean>(false);

  // Handlers for Modal
  const showModal = () => setIsModalVisible(true);
  const handleOk = () => setIsModalVisible(false);
  const handleCancel = () => setIsModalVisible(false);

  // Fetch models
  useEffect(() => {
    setModels([]);
    const fetchModels = async () => {
      if (filterValues.selectedMakes.length > 0) {
        const data = await getModels({ ...filterValues, isClassifieds });
        setModels(data || []); // Use an empty array if data is undefined
      } else {
        setModels([]);
      }
    };
    fetchModels();
  }, [
    filterValues.selectedMakes,
    filterValues.websites,
    filterValues.reserveStatus,
    filterValues.sellerType,
    isClassifieds,
  ]);

  // Fetch makes
  useEffect(() => {
    setMakes([]);
    const fetchMakes = async () => {
      const data = await getMakes({ ...filterValues, isClassifieds });
      setMakes(data || []);
    };
    fetchMakes();
  }, [
    filterValues.selectedMakes,
    filterValues.selectedModels,
    filterValues.reserveStatus,
    filterValues.sellerType,
    isClassifieds,
  ]);

  // Fetch websites
  useEffect(() => {
    setWebsites([]);
    const fetchSites = async () => {
      const data = await getWebsites({ ...filterValues, isClassifieds });
      setWebsites(data || []);
    };
    fetchSites();
  }, [
    filterValues.selectedMakes,
    filterValues.selectedModels,
    filterValues.reserveStatus,
    filterValues.sellerType,
    isClassifieds,
  ]);

  // Fetch Top Picks cars
  const fetchTopPicksCars = async () => {
    try {
      setLoadingTopPicks(true);
      setError(null);
      const params = {
        page: 1,
        itemsPerPage: 6,
        searchTerm,
        sortField,
        sortOrder,
        includeBuyersFee,
        starredOnly: false,
        current: true,
        ...customFilterValues,
      };
      const data = await searchCars(params);
      if (!data) return;
      const cars = formatCarData(data?.cars || []);
      setTopPicksCars(cars || []);
      setTopPicksLength(data?.cars?.length || 0);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch top picks.");
    } finally {
      setLoadingTopPicks(false);
    }
  };

  // Fetch Classifieds cars
  const fetchClassifiedsCars = async () => {
    try {
      setLoadingClassifieds(true);
      setError(null);
      // Customize this filter for classifieds
      const classifiedsFilters = {
        ...filterValues,
        location: "48120",
        distance: "500",
        startYear: "2010",
        websites: ["Facebook", "Craigslist", "Autotrader"],
        sellerType: ["Private"],
        selectedMakes: ["Chevrolet", "Dodge", "Ram", "Jeep", "BMW", "Ford"]
      };
      const params = {
        page: 1,
        itemsPerPage: 6,
        searchTerm,
        sortField,
        sortOrder,
        includeBuyersFee,
        starredOnly: false,
        current: true,
        ...classifiedsFilters,
      };

      // Use the new mixed endpoint
      const data = await getMixedClassifieds(params);
      if (!data) return;

      // Use the cars directly from the backend (10 from current + 10 random classifieds)
      const cars = data?.cars || [];

      // Format the car data and update state
      const formattedCars = formatCarData(cars);
      setClassifiedsCars(formattedCars || []);
      setClassifiedsLength(cars.length || 0);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch classifieds.");
    } finally {
      setLoadingClassifieds(false);
    }
  };

  // Fetch Wholesale cars
  const fetchWholesaleCars = async () => {
    try {
      setLoadingWholesale(true);
      setError(null);
      // Customize this filter for wholesale
      const wholesaleFilters = {
        ...filterValues,
        location: "48120",
        distance: "10",
        startYear: "2010",
      };
      const params = {
        page: 1,
        itemsPerPage: 6,
        searchTerm,
        sortField,
        sortOrder,
        includeBuyersFee,
        starredOnly: false,
        current: true,
        ...wholesaleFilters,
      };
      const data = await searchCars(params);
      if (!data) return;
      const cars = formatCarData(data?.cars);
      setWholesaleCars(cars || []);
      setWholesaleLength(data?.cars?.length || 0);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch wholesale auctions.");
    } finally {
      setLoadingWholesale(false);
    }
  };

  // Fetch Credit cars
  const fetchFordCreditCars = async () => {
    try {
      setLoadingFordCredit(true);
      setError(null);
      const params = {
        page: 1,
        itemsPerPage: 6,
        searchTerm,
        sortField,
        sortOrder,
        includeBuyersFee,
        starredOnly: false,
        current: true,
        ...fordCustomFilterValues,
      };
      const data = await searchCars(params);
      if (!data) return;
      const cars = formatCarData(data?.cars);
      setFordCreditCars(cars || []);
      setFordCreditLength(data?.cars?.length || 0);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch credit auctions.");
    } finally {
      setLoadingFordCredit(false);
    }
  };



  // Fetch data based on active tab
  useEffect(() => {
    if (activeTab === "topPicks") {
      fetchTopPicksCars();
    } else if (activeTab === "classifieds") {
      fetchClassifiedsCars();
    } else if (activeTab === "wholesale") {
      // fetchWholesaleCars();
    } else if (activeTab === "fordCredit") {
      fetchFordCreditCars();
    }

  }, [activeTab]);

  // Handlers
  const handleSearchTermChange = (term: string) => {
    setSearchTerm(term);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleFilterChange = (
    filterKey: keyof typeof filterValues,
    values: string[]
  ) => {
    setFilterValues((prevFilters) => ({ ...prevFilters, [filterKey]: values }));
  };

  const onNavigateToDetails = () => {
    saveTableState();
  };

  const handleSearch = () => {
    const serializedFilters = JSON.stringify(filterValues);
    const queryParams = new URLSearchParams({
      searchTerm: searchTerm || "",
      filterValues: serializedFilters,
      page: "1",
      itemsPerPage: itemsPerPage.toString(),
    });
    saveTableState();
    // Navigate to the homepage with filters if desired
    navigate(`/`);
    // OR: navigate(`/?${queryParams.toString()}`);
  };

  const resetFilters = () => {
    setFilterValues({
      selectedMakes: [],
      selectedModels: [],
      websites: [],
      reserveStatus: [],
      sellerType: [],
      pastToggle: [],
    });
  };

  // Handle tab change
  const handleTabChange = (key: string) => {

    saveTableState();
    setActiveTab(key as TabKey);
  };

  // Drawer togglers
  const openDrawer = () => {
    setDrawerVisible(true);
  };

  const closeDrawer = () => {
    setDrawerVisible(false);
  };

  // Render conditionally based on isMobile
  return (
    <>
      {isMobile ? (
        <div className="landing-page-container mobile-version">
          <TopBar title="" />

          <div className="content">
            <SearchFiltersSection
              searchTerm={searchTerm}
              onSearchTermChange={handleSearchTermChange}
              filterValues={filterValues}
              handleFilterChange={handleFilterChange}
              handleSearch={handleSearch}
              makes={makes}
              models={models}
              websites={displayedWebsites}
              showModal={showModal}
              isClassifieds={isClassifieds}
              setIsClassifieds={setIsClassifieds}
              resetFilters={resetFilters}
            />

            {/* Button to open filters drawer on mobile */}
            <div style={{ textAlign: "right", margin: "10px 0" }}>
              <Button icon={<MenuOutlined />} onClick={openDrawer}>
                Filters
              </Button>
            </div>

            {/* Drawer containing filters for mobile */}
            <Drawer
              title="Filters"
              placement="right"
              onClose={closeDrawer}
              open={drawerVisible}
            >
              {/* Mobile filters content */}
            </Drawer>

            {/* Tabs for mobile */}
            <div className="tab-selector-container">
              <Tabs activeKey={activeTab} onChange={handleTabChange} centered>
                <TabPane tab="Auctions" key="topPicks" />
                <TabPane tab="Classifieds" key="classifieds" />
                <TabPane tab="Wholesale" key="wholesale" />
                {/* <TabPane tab="Ford Credit" key="fordCredit" /> */}
                <TabPane tab="Trade-ins" key="tradeIns" />
              </Tabs>
            </div>

            {/* Modal */}
            <Modal
              title="About Scout"
              visible={isModalVisible}
              onOk={handleOk}
              onCancel={handleCancel}
              footer={[
                <Button key="close" type="primary" onClick={handleCancel}>
                  Close
                </Button>,
              ]}
            >
              <p>
                Scout is a Pacer product that aggregates auctions from multiple
                sites. (Mobile Explanation)
              </p>
            </Modal>

            {/* Tab Content */}
            {activeTab === "topPicks" && (
              <TopPicksSection
                loading={loadingTopPicks}
                error={error}
                cars={topPicksCars}
                carsLength={topPicksLength}
                starToggle={starToggle}
                setStarChanged={setStarChanged}
                setDataSource={setTopPicksCars}
                onNavigateToDetails={onNavigateToDetails}
              />
            )}

            {activeTab === "classifieds" && (
              <ClassifiedsSection
                loading={loadingClassifieds}
                error={error}
                cars={classifiedsCars}
                carsLength={classifiedsLength}
                starToggle={starToggle}
                setStarChanged={setStarChanged}
                setDataSource={setClassifiedsCars}
                onNavigateToDetails={onNavigateToDetails}
              />
            )}

            {activeTab === "wholesale" && (
              <div style={{ textAlign: "center", padding: "50px 20px" }}>
                <Title level={1}>Coming Soon</Title>
                <p style={{ fontSize: "18px", color: "#666" }}>
                  Wholesale inventory from other Pacer users will show up here.
                </p>
              </div>
            )}

            {activeTab === "tradeIns" && (
              <div style={{ textAlign: "center", padding: "50px 20px" }}>
                <Title level={1}>Coming Soon</Title>
                <p style={{ fontSize: "18px", color: "#666", marginBottom: "20px" }}>
                  To run a trade-in analysis, use our Reports product.
                </p>
                <Button
                  type="primary"
                  size="large"
                  href="https://kickthetires.ai/#/create-report"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: "none" }}
                >
                  Go to Reports
                </Button>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="landing-page-container">
          <TopBar title="" />
          <div className="content">
            <SearchFiltersSection
              searchTerm={searchTerm}
              onSearchTermChange={handleSearchTermChange}
              filterValues={filterValues}
              handleFilterChange={handleFilterChange}
              handleSearch={handleSearch}
              makes={makes}
              models={models}
              websites={displayedWebsites}
              showModal={showModal}
              isClassifieds={isClassifieds}
              setIsClassifieds={setIsClassifieds}
              resetFilters={resetFilters}
            />

            {/* Modal */}
            <Modal
              title="About Scout"
              visible={isModalVisible}
              onOk={handleOk}
              onCancel={handleCancel}
              footer={[
                <Button key="close" type="primary" onClick={handleCancel}>
                  Close
                </Button>,
              ]}
            >
              <p>
                Scout is a Pacer product that aggregates auctions from multiple
                sites. For each auction, Scout provides key information about
                the car and includes estimates for potential profit or loss
                based on historical data.
              </p>
            </Modal>

            <div style={{ marginTop: "25px" }} />

            {/* Tabs for desktop */}
            <div className="tab-selector-container">
              <Tabs activeKey={activeTab} onChange={handleTabChange} centered>
                <TabPane tab="Auctions" key="topPicks" />
                <TabPane tab="Classifieds" key="classifieds" />
                <TabPane tab="Wholesale" key="wholesale" />
                {/* <TabPane tab="Ford Credit" key="fordCredit" /> */}
                <TabPane tab="Trade-ins" key="tradeIns" />
              </Tabs>
            </div>

            {/* Tab Content */}
            {activeTab === "topPicks" && (
              <TopPicksSection
                loading={loadingTopPicks}
                error={error}
                cars={topPicksCars}
                carsLength={topPicksLength}
                starToggle={starToggle}
                setStarChanged={setStarChanged}
                setDataSource={setTopPicksCars}
                onNavigateToDetails={onNavigateToDetails}
              />
            )}

            {activeTab === "classifieds" && (
              <ClassifiedsSection
                loading={loadingClassifieds}
                error={error}
                cars={classifiedsCars}
                carsLength={classifiedsLength}
                starToggle={starToggle}
                setStarChanged={setStarChanged}
                setDataSource={setClassifiedsCars}
                onNavigateToDetails={onNavigateToDetails}
              />
            )}

            {activeTab === "wholesale" && (
              <div style={{ textAlign: "center", padding: "50px 20px" }}>
                <Title level={1}>Coming Soon</Title>
                <p style={{ fontSize: "18px", color: "#666" }}>
                  Wholesale inventory from other Pacer users will show up here.
                </p>
              </div>
            )}
            {activeTab === "fordCredit" && (
              <FordCreditSection
                loading={loadingFordCredit}
                error={error}
                cars={fordCreditCars}
                carsLength={fordCreditLength}
                starToggle={starToggle}
                setStarChanged={setStarChanged}
                setDataSource={setFordCreditCars}
                onNavigateToDetails={onNavigateToDetails}
              />
            )}
            {activeTab === "tradeIns" && (
              // <TradeInsSection
              //   loading={loadingTradeIns}
              //   error={error}
              //   cars={tradeInsCars}
              //   carsLength={tradeInsLength}
              //   starToggle={starToggle}
              //   setStarChanged={setStarChanged}
              //   setDataSource={setFordCreditCars}
              //   onNavigateToDetails={onNavigateToDetails}
              // />
              <div style={{ textAlign: "center", padding: "50px 20px" }}>
                <Title level={1}>Coming Soon</Title>
                <p style={{ fontSize: "18px", color: "#666" }}>
                  To run a trade-in analysis, use our <a href="https://reports.pacer.co/#/create-report" target="_blank" rel="noopener noreferrer">Reports</a> product.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};
export default Landing;
